import {
  formatRevisionEtag,
  parseRevisionEtag,
  type ProjectDocument,
  type ProjectRole,
  type SaveProjectRequest,
  type SaveProjectViewRequest,
} from '@bigint-project/cloud-contracts';
import { BIGINT_PROJECT_CLOUD_EXTENSION_KEY } from '@bigint-project/calendar-catalog';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';

import type { CloudApi, Etagged } from '../api/client';
import { ApiError, createClientOperationId, errorMessage, isConflictError } from '../api/client';
import type { EditorHostComponent } from '../platform/EditorHost';
import { createCloudCollaborationPort } from '../platform/collaboration';
import { downloadBproj, toBprojFile } from '../platform/file-exchange';
import { CalendarPanel } from './CalendarPanel';
import { ConflictPanel } from './ConflictPanel';
import { RevisionsPanel } from './RevisionsPanel';
import { SharingPanel } from './SharingPanel';
import { ErrorMessage, Loading } from './Status';
import { LogoutButton } from './LogoutButton';

type WorkspaceTab = 'editor' | 'revisions' | 'sharing' | 'calendar';
type SaveState = 'saved' | 'unsaved' | 'saving' | 'conflict' | 'offline' | 'failed';

interface PendingSave {
  readonly generation: number;
  readonly document: ProjectDocument;
  readonly etag: string;
  readonly request: SaveProjectRequest;
  readonly retries: number;
}

interface PendingViewSave {
  readonly generation: number;
  readonly request: SaveProjectViewRequest;
  readonly etag: string;
  readonly retries: number;
}

interface ProjectWorkspaceProps {
  readonly api: CloudApi;
  readonly projectId: string;
  /** 判斷留言／附件能不能由自己刪除，伺服器仍會再驗一次。 */
  readonly currentUserId: string;
  readonly EditorHost: EditorHostComponent;
  readonly onBack: () => void;
  readonly onOpenProject: (id: string) => void;
  readonly onLoggedOut: () => void;
}

const SAVE_LABELS: Record<SaveState, string> = {
  saved: '已儲存',
  unsaved: '未儲存',
  saving: '儲存中…',
  conflict: '發生衝突',
  offline: '離線，尚未儲存',
  failed: '儲存失敗',
};

const VIEW_SAVE_LABELS: Record<SaveState, string> = {
  saved: '檢視已儲存',
  unsaved: '檢視未儲存',
  saving: '檢視儲存中…',
  conflict: '檢視衝突',
  offline: '檢視離線',
  failed: '檢視儲存失敗',
};

function saveableUnknownFields(
  fields: ProjectDocument['unknownTopLevelFields'],
): ProjectDocument['unknownTopLevelFields'] {
  return Object.fromEntries(
    Object.entries(fields).filter(([key]) => key !== BIGINT_PROJECT_CLOUD_EXTENSION_KEY),
  );
}

export function ProjectWorkspace({ api, projectId, currentUserId, EditorHost, onBack, onOpenProject, onLoggedOut }: ProjectWorkspaceProps): ReactElement {
  const [document, setDocument] = useState<ProjectDocument | null>(null);
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<WorkspaceTab>('editor');
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [viewSaveState, setViewSaveState] = useState<SaveState>('saved');
  const [localGeneration, setLocalGeneration] = useState(0);
  const [conflictDocument, setConflictDocument] = useState<ProjectDocument | null>(null);
  const [conflictBusy, setConflictBusy] = useState(false);
  const [authenticationRequired, setAuthenticationRequired] = useState(false);

  const documentRef = useRef<ProjectDocument | null>(null);
  const etagRef = useRef('');
  const viewEtagRef = useRef('');
  const roleRef = useRef<ProjectRole>('viewer');
  const generationRef = useRef(0);
  const savedGenerationRef = useRef(0);
  const savingRef = useRef(false);
  const conflictRef = useRef(false);
  const authenticationRequiredRef = useRef(false);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const viewGenerationRef = useRef(0);
  const viewEpochRef = useRef(0);
  const savedViewGenerationRef = useRef(0);
  const viewSavingRef = useRef(false);
  const viewConflictRef = useRef(false);
  const pendingViewSaveRef = useRef<PendingViewSave | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const autosaveTimerRef = useRef<number | null>(null);
  const viewRetryTimerRef = useRef<number | null>(null);
  const projectIdRef = useRef(projectId);
  const workspaceEpochRef = useRef(0);
  const mountedRef = useRef(true);

  const applyRemote = useCallback((result: Etagged<ProjectDocument>, replaceView = false): void => {
    if (!mountedRef.current || result.data.id !== projectIdRef.current) return;
    const current = documentRef.current;
    let nextDocument = result.data;
    if (!replaceView && current !== null) {
      nextDocument = { ...result.data, view: current.view };
    }
    documentRef.current = nextDocument;
    etagRef.current = result.etag;
    roleRef.current = nextDocument.role;
    generationRef.current += 1;
    savedGenerationRef.current = generationRef.current;
    conflictRef.current = false;
    authenticationRequiredRef.current = false;
    pendingSaveRef.current = null;
    setDocument(nextDocument);
    setEtag(result.etag);
    setLocalGeneration(generationRef.current);
    setConflictDocument(null);
    setSaveState('saved');
    if (replaceView) {
      viewEpochRef.current += 1;
      viewEtagRef.current = result.viewEtag ?? formatRevisionEtag(result.data.view.revision);
      viewGenerationRef.current += 1;
      savedViewGenerationRef.current = viewGenerationRef.current;
      viewConflictRef.current = false;
      viewSavingRef.current = false;
      pendingViewSaveRef.current = null;
      setViewSaveState('saved');
    }
  }, []);

  const loadProject = useCallback(async (): Promise<void> => {
    setError(null);
    const result = await api.getProject(projectId);
    if (projectIdRef.current === projectId) applyRemote(result, true);
  }, [api, applyRemote, projectId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const epoch = workspaceEpochRef.current + 1;
    workspaceEpochRef.current = epoch;
    projectIdRef.current = projectId;
    if (autosaveTimerRef.current !== null) window.clearTimeout(autosaveTimerRef.current);
    if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    if (viewRetryTimerRef.current !== null) window.clearTimeout(viewRetryTimerRef.current);
    autosaveTimerRef.current = null;
    retryTimerRef.current = null;
    viewRetryTimerRef.current = null;
    documentRef.current = null;
    etagRef.current = '';
    viewEtagRef.current = '';
    roleRef.current = 'viewer';
    generationRef.current = 0;
    savedGenerationRef.current = 0;
    savingRef.current = false;
    conflictRef.current = false;
    authenticationRequiredRef.current = false;
    pendingSaveRef.current = null;
    viewGenerationRef.current = 0;
    viewEpochRef.current += 1;
    savedViewGenerationRef.current = 0;
    viewSavingRef.current = false;
    viewConflictRef.current = false;
    pendingViewSaveRef.current = null;
    setDocument(null);
    setEtag('');
    setError(null);
    setConflictDocument(null);
    setAuthenticationRequired(false);
    setLocalGeneration(0);
    setSaveState('saved');
    setViewSaveState('saved');
    setLoading(true);
    void api.getProject(projectId).then(
      (result) => {
        if (!cancelled && workspaceEpochRef.current === epoch) applyRemote(result, true);
      },
      (reason: unknown) => {
        if (!cancelled) setError(errorMessage(reason));
      },
    ).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api, applyRemote, projectId]);

  const flushSave = useCallback(async function runSave(
    expectedEpoch = workspaceEpochRef.current,
  ): Promise<void> {
    const epoch = expectedEpoch;
    const targetProjectId = projectId;
    if (
      !mountedRef.current ||
      workspaceEpochRef.current !== epoch ||
      projectIdRef.current !== targetProjectId
    ) return;
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (savingRef.current) return;
    const current = documentRef.current;
    if (
      current === null ||
      roleRef.current === 'viewer' ||
      conflictRef.current ||
      authenticationRequiredRef.current ||
      generationRef.current <= savedGenerationRef.current
    ) return;

    const pending = pendingSaveRef.current ?? {
      generation: generationRef.current,
      document: current,
      etag: etagRef.current,
      request: {
        formatVersion: current.formatVersion,
        project: current.project,
        unknownTopLevelFields: saveableUnknownFields(current.unknownTopLevelFields),
        clientOperationId: createClientOperationId(),
        reason: 'web-autosave',
      },
      retries: 0,
    } satisfies PendingSave;
    pendingSaveRef.current = pending;
    savingRef.current = true;
    setSaveState('saving');
    setError(null);
    let saved = false;
    let retryScheduled = false;
    try {
      const result = await api.saveProject(targetProjectId, pending.request, pending.etag);
      if (
        !mountedRef.current ||
        workspaceEpochRef.current !== epoch ||
        projectIdRef.current !== targetProjectId
      ) return;
      pendingSaveRef.current = null;
      etagRef.current = result.etag;
      setEtag(result.etag);
      savedGenerationRef.current = pending.generation;
      saved = true;

      if (generationRef.current === pending.generation) {
        const latestDocument = documentRef.current ?? pending.document;
        const savedDocument = {
          ...result.data,
          name: latestDocument.project.name,
          project: latestDocument.project,
          view: latestDocument.view,
        };
        documentRef.current = savedDocument;
        roleRef.current = savedDocument.role;
        setDocument(savedDocument);
        setSaveState('saved');
      } else {
        const latest = documentRef.current;
        if (latest !== null) {
          const rebased: ProjectDocument = {
            ...latest,
            serverRevision: result.data.serverRevision,
            updatedAt: result.data.updatedAt,
          };
          documentRef.current = rebased;
          setDocument(rebased);
        }
        setSaveState('unsaved');
      }
    } catch (reason) {
      if (
        !mountedRef.current ||
        workspaceEpochRef.current !== epoch ||
        projectIdRef.current !== targetProjectId
      ) return;
      if (isConflictError(reason)) {
        pendingSaveRef.current = null;
        conflictRef.current = true;
        setConflictDocument(documentRef.current ?? pending.document);
        setSaveState('conflict');
      } else {
        if (reason instanceof ApiError && reason.status === 401) {
          authenticationRequiredRef.current = true;
          setAuthenticationRequired(true);
        }
        setSaveState(navigator.onLine ? 'failed' : 'offline');
        setError(errorMessage(reason));
        const retryable = !(reason instanceof ApiError) || reason.status === 429 || reason.status >= 500;
        if (retryable && pending.retries < 3) {
          const nextPending: PendingSave = { ...pending, retries: pending.retries + 1 };
          pendingSaveRef.current = nextPending;
          const delay = 1_000 * (2 ** pending.retries);
          if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
          const timer = window.setTimeout(() => {
            if (retryTimerRef.current !== timer) return;
            retryTimerRef.current = null;
            if (
              !mountedRef.current ||
              workspaceEpochRef.current !== epoch ||
              projectIdRef.current !== targetProjectId
            ) return;
            void runSave(epoch);
          }, delay);
          retryTimerRef.current = timer;
          retryScheduled = true;
        } else {
          pendingSaveRef.current = null;
        }
      }
    } finally {
      if (
        !mountedRef.current ||
        workspaceEpochRef.current !== epoch ||
        projectIdRef.current !== targetProjectId
      ) return;
      savingRef.current = false;
      const hasNewerGeneration = generationRef.current > pending.generation;
      if (
        !conflictRef.current &&
        !authenticationRequiredRef.current &&
        hasNewerGeneration &&
        (saved || !retryScheduled)
      ) {
        void runSave(epoch);
      }
    }
  }, [api, projectId]);

  const saveNow = useCallback(async (): Promise<void> => {
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    await flushSave(workspaceEpochRef.current);
  }, [flushSave]);

  const flushViewSave = useCallback(async function runViewSave(
    expectedWorkspaceEpoch = workspaceEpochRef.current,
    expectedViewEpoch = viewEpochRef.current,
  ): Promise<void> {
    const current = documentRef.current;
    const epoch = expectedWorkspaceEpoch;
    const viewEpoch = expectedViewEpoch;
    const targetProjectId = projectId;
    if (
      !mountedRef.current ||
      workspaceEpochRef.current !== epoch ||
      viewEpochRef.current !== viewEpoch ||
      projectIdRef.current !== targetProjectId ||
      current === null ||
      viewConflictRef.current ||
      authenticationRequiredRef.current ||
      viewGenerationRef.current <= savedViewGenerationRef.current
    ) return;
    if (viewRetryTimerRef.current !== null) {
      window.clearTimeout(viewRetryTimerRef.current);
      viewRetryTimerRef.current = null;
    }
    if (viewSavingRef.current) return;

    const pending = pendingViewSaveRef.current ?? {
      generation: viewGenerationRef.current,
      etag: viewEtagRef.current,
      request: {
        known: current.view.known,
        unknown: current.view.unknown,
        clientOperationId: createClientOperationId(),
      },
      retries: 0,
    } satisfies PendingViewSave;
    pendingViewSaveRef.current = pending;
    viewSavingRef.current = true;
    setViewSaveState('saving');
    let saved = false;
    let retryScheduled = false;
    try {
      const nextEtag = await api.saveProjectView(targetProjectId, pending.request, pending.etag);
      if (
        !mountedRef.current ||
        workspaceEpochRef.current !== epoch ||
        viewEpochRef.current !== viewEpoch ||
        projectIdRef.current !== targetProjectId
      ) return;
      pendingViewSaveRef.current = null;
      viewEtagRef.current = nextEtag;
      savedViewGenerationRef.current = pending.generation;
      saved = true;

      const latest = documentRef.current;
      if (latest !== null) {
        const nextRevision = parseRevisionEtag(nextEtag) ?? latest.view.revision;
        const withViewRevision = {
          ...latest,
          view: { ...latest.view, revision: nextRevision },
        };
        documentRef.current = withViewRevision;
        setDocument(withViewRevision);
      }
      setViewSaveState(viewGenerationRef.current === pending.generation ? 'saved' : 'unsaved');
    } catch (reason) {
      if (
        !mountedRef.current ||
        workspaceEpochRef.current !== epoch ||
        viewEpochRef.current !== viewEpoch ||
        projectIdRef.current !== targetProjectId
      ) return;
      if (isConflictError(reason)) {
        pendingViewSaveRef.current = null;
        viewConflictRef.current = true;
        setViewSaveState('conflict');
        setError('同一帳號已在其他瀏覽器更新檢視設定；目前檢視未覆蓋遠端版本，請重新載入專案。');
      } else {
        if (reason instanceof ApiError && reason.status === 401) {
          authenticationRequiredRef.current = true;
          setAuthenticationRequired(true);
        }
        setViewSaveState(navigator.onLine ? 'failed' : 'offline');
        setError(errorMessage(reason));
        const retryable = !(reason instanceof ApiError) || reason.status === 429 || reason.status >= 500;
        if (retryable && pending.retries < 3) {
          pendingViewSaveRef.current = { ...pending, retries: pending.retries + 1 };
          const delay = 1_000 * (2 ** pending.retries);
          if (viewRetryTimerRef.current !== null) window.clearTimeout(viewRetryTimerRef.current);
          const timer = window.setTimeout(() => {
            if (viewRetryTimerRef.current !== timer) return;
            viewRetryTimerRef.current = null;
            if (
              !mountedRef.current ||
              workspaceEpochRef.current !== epoch ||
              viewEpochRef.current !== viewEpoch ||
              projectIdRef.current !== targetProjectId
            ) return;
            void runViewSave(epoch, viewEpoch);
          }, delay);
          viewRetryTimerRef.current = timer;
          retryScheduled = true;
        } else {
          pendingViewSaveRef.current = null;
        }
      }
    } finally {
      if (
        !mountedRef.current ||
        workspaceEpochRef.current !== epoch ||
        viewEpochRef.current !== viewEpoch ||
        projectIdRef.current !== targetProjectId
      ) return;
      viewSavingRef.current = false;
      const hasNewerGeneration = viewGenerationRef.current > pending.generation;
      if (
        !viewConflictRef.current &&
        !authenticationRequiredRef.current &&
        hasNewerGeneration &&
        (saved || !retryScheduled)
      ) void runViewSave(epoch, viewEpoch);
    }
  }, [api, projectId]);

  useEffect(() => () => {
    if (autosaveTimerRef.current !== null) window.clearTimeout(autosaveTimerRef.current);
    if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    if (viewRetryTimerRef.current !== null) window.clearTimeout(viewRetryTimerRef.current);
  }, []);

  useEffect(() => {
    if (
      document === null ||
      document.role === 'viewer' ||
      localGeneration <= savedGenerationRef.current ||
      conflictRef.current ||
      authenticationRequiredRef.current
    ) return undefined;
    const epoch = workspaceEpochRef.current;
    const timer = window.setTimeout(() => {
      if (autosaveTimerRef.current === timer) autosaveTimerRef.current = null;
      void flushSave(epoch);
    }, 2_000);
    autosaveTimerRef.current = timer;
    return () => {
      window.clearTimeout(timer);
      if (autosaveTimerRef.current === timer) autosaveTimerRef.current = null;
    };
  }, [document, flushSave, localGeneration]);

  useEffect(() => {
    if (
      document === null ||
      viewGenerationRef.current <= savedViewGenerationRef.current ||
      viewConflictRef.current ||
      authenticationRequiredRef.current
    ) return undefined;
    const epoch = workspaceEpochRef.current;
    const viewEpoch = viewEpochRef.current;
    const timer = window.setTimeout(() => void flushViewSave(epoch, viewEpoch), 2_000);
    return () => window.clearTimeout(timer);
  }, [document, flushViewSave]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent): void => {
      if (
        generationRef.current <= savedGenerationRef.current &&
        viewGenerationRef.current <= savedViewGenerationRef.current
      ) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  const changeProject = (project: ProjectDocument['project']): void => {
    if (documentRef.current === null || roleRef.current === 'viewer' || conflictRef.current) return;
    const next: ProjectDocument = {
      ...documentRef.current,
      name: project.name,
      project,
    };
    documentRef.current = next;
    generationRef.current += 1;
    setDocument(next);
    setLocalGeneration(generationRef.current);
    setSaveState('unsaved');
  };

  const changeView = (known: NonNullable<ProjectDocument['view']['known']>): void => {
    const current = documentRef.current;
    if (current === null || viewConflictRef.current) return;
    const next: ProjectDocument = {
      ...current,
      view: { ...current.view, known },
    };
    documentRef.current = next;
    viewGenerationRef.current += 1;
    setDocument(next);
    setViewSaveState('unsaved');
  };

  const reloadConflict = async (): Promise<void> => {
    setConflictBusy(true);
    setError(null);
    try {
      await loadProject();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setConflictBusy(false);
    }
  };

  const saveConflictCopy = async (): Promise<void> => {
    const local = conflictDocument;
    if (local === null) return;
    setConflictBusy(true);
    setError(null);
    try {
      const copy = await api.importProject(toBprojFile(local));
      onOpenProject(copy.id);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setConflictBusy(false);
    }
  };

  const role = document?.role ?? null;
  const collaboration = useMemo(
    () => (role === null
      ? undefined
      : createCloudCollaborationPort({ api, projectId, role, currentUserId })),
    [api, currentUserId, projectId, role],
  );

  if (loading) return <Loading>正在載入雲端專案…</Loading>;
  if (document === null) {
    return (
      <main className="page-shell">
        {error !== null && <ErrorMessage message={error} />}
        <button type="button" onClick={onBack}>返回專案清單</button>
      </main>
    );
  }

  const readOnly = document.role === 'viewer';
  const mutationBlocked =
    conflictDocument !== null ||
    saveState === 'saving' ||
    localGeneration > savedGenerationRef.current;

  return (
    <main className="workspace">
      <header className="workspace-header">
        <button type="button" className="secondary" onClick={onBack}>← 專案清單</button>
        <div className="workspace-title">
          <h1>{document.name}</h1>
          <span className={`role-badge role-${document.role}`}>{document.role}</span>
          <span className={`save-status save-${saveState}`} role="status" aria-live="polite">
            {SAVE_LABELS[saveState]}
          </span>
          <span className={`save-status save-${viewSaveState}`} aria-live="polite">
            {VIEW_SAVE_LABELS[viewSaveState]}
          </span>
        </div>
        <div className="workspace-actions">
          <span className="muted">rev {document.serverRevision}</span>
          <button type="button" className="secondary" onClick={() => downloadBproj(document)}>匯出 .bproj</button>
          <button
            type="button"
            disabled={
              readOnly ||
              authenticationRequired ||
              saveState === 'saving' ||
              saveState === 'conflict' ||
              localGeneration <= savedGenerationRef.current
            }
            onClick={() => void saveNow()}
          >
            立即儲存
          </button>
          <LogoutButton api={api} onLoggedOut={onLoggedOut} />
        </div>
      </header>

      <nav className="workspace-tabs" aria-label="專案功能">
        {([
          ['editor', '排程'],
          ['revisions', '版本'],
          ['sharing', '分享'],
          ['calendar', '行事曆'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-current={tab === value ? 'page' : undefined}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </nav>

      {error !== null && <ErrorMessage message={error} />}
      {authenticationRequired && (
        <section className="reauth-panel" role="alert">
          <span>登入狀態已失效；自動儲存已停止。</span>
          <button
            type="button"
            onClick={() => window.location.assign(api.loginUrl(`${window.location.pathname}${window.location.search}`))}
          >
            重新登入
          </button>
        </section>
      )}
      {conflictDocument !== null && (
        <ConflictPanel
          localProject={conflictDocument}
          busy={conflictBusy}
          onExport={() => downloadBproj(conflictDocument)}
          onReload={() => void reloadConflict()}
          onSaveCopy={() => void saveConflictCopy()}
        />
      )}

      <div className="workspace-content">
        {tab === 'editor' && (
          <EditorHost
            document={document}
            readOnly={readOnly || conflictDocument !== null}
            onChange={changeProject}
            onViewChange={changeView}
            onSave={saveNow}
            collaboration={collaboration}
          />
        )}
        {tab === 'revisions' && (
          <RevisionsPanel
            api={api}
            projectId={projectId}
            role={document.role}
            etag={etag}
            mutationBlocked={mutationBlocked}
            onRestored={applyRemote}
          />
        )}
        {tab === 'sharing' && (
          <SharingPanel
            api={api}
            projectId={projectId}
            projectName={document.name}
            role={document.role}
            onOwnerTransferred={() => void loadProject()}
          />
        )}
        {tab === 'calendar' && (
          <CalendarPanel
            api={api}
            project={document}
            etag={etag}
            mutationBlocked={mutationBlocked}
            onApplied={applyRemote}
          />
        )}
      </div>
    </main>
  );
}
